// src/components/Admin/Email/EmailSidebar.tsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InboxIcon from "@mui/icons-material/Inbox";
import SendIcon from "@mui/icons-material/Send";
import DraftsIcon from "@mui/icons-material/Drafts";
import TrashIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { EmailHierarchy, EmailThreadGroup } from "../../../contexts/EmailContext";

interface EmailSidebarProps {
  emailHierarchy: EmailHierarchy;
  selectedAccountType: string;
  selectedFolder: string;
  onFolderSelect: (accountType: string, folder: string) => void;
}

const dynamicFolderIcons: Record<string, JSX.Element> = {
  inbox: <InboxIcon color="primary" />,
  sent: <SendIcon color="primary" />,
  drafts: <DraftsIcon color="primary" />,
};

const staticFolders = [
  { key: "trash", label: "Trash", icon: <TrashIcon color="error" /> },
  { key: "settings", label: "Settings", icon: <SettingsIcon color="action" /> },
];

const EmailSidebar: React.FC<EmailSidebarProps> = ({
  emailHierarchy,
  selectedAccountType,
  selectedFolder,
  onFolderSelect,
}) => {
  return (
    <Box sx={{ backgroundColor: "background.paper", height: "100%", overflowY: "auto" }}>
      {Object.entries(emailHierarchy).map(([accountType, folders]) => (
        <Accordion
          key={accountType}
          defaultExpanded={accountType === selectedAccountType}
          sx={{ boxShadow: 0, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ "&:hover": { backgroundColor: "grey.100" }, px: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {accountType}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List>
              {Object.entries(folders).map(
                ([folder, group]: [string, EmailThreadGroup]) => (
                  <ListItemButton
                    key={folder}
                    selected={selectedAccountType === accountType && selectedFolder === folder}
                    onClick={() => onFolderSelect(accountType, folder)}
                    sx={{ pl: 4 }}
                  >
                    {dynamicFolderIcons[folder] || null}
                    <ListItemText
                      primary={`${folder.charAt(0).toUpperCase() + folder.slice(1)} (${group.threadCount})`}
                      sx={{ ml: 1 }}
                    />
                  </ListItemButton>
                )
              )}
              <Divider sx={{ my: 1 }} />
              {staticFolders.map((item) => (
                <ListItemButton
                  key={item.key}
                  selected={selectedAccountType === accountType && selectedFolder === item.key}
                  onClick={() => onFolderSelect(accountType, item.key)}
                  sx={{ pl: 4 }}
                >
                  {item.icon}
                  <ListItemText primary={item.label} sx={{ ml: 1 }} />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default EmailSidebar;
